import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTenant } from '../../contexts/TenantContext';
import { DISABLED_FEATURES } from '../../config/api';
import {
  User,
  LogOut,
  Settings,
  BookOpen,
  Award,
  LayoutDashboard,
  Menu,
  X,
  ChevronDown,
  Heart,
  Beaker,
  Building2,
  GraduationCap,
  Globe
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { getInitials } from '../../lib/utils';
import NotificationCenter from '../NotificationCenter';

// Cerebro Logo - Teal/Gold Neural Brain Hexagon
const CEREBRO_LOGO = "https://customer-assets.emergentagent.com/job_cpd-compass/artifacts/km6w43n1_Teal%20Cerebro%20Transp.png";
const DEFAULT_LOGO = CEREBRO_LOGO;

export const Header = ({ hideLogo = false }) => {
  const { user, logout, isAdmin, isAuthenticated } = useAuth();
  const { tenant } = useTenant();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  // Helper to check if feature is enabled (respects DISABLED_FEATURES override)
  const isFeatureEnabled = (feature) => {
    // Check code-level disable first (takes priority)
    if (DISABLED_FEATURES[feature]) return false;
    // Then check tenant settings
    return tenant.features?.[feature] !== false;
  };

  // Build nav links based on tenant feature flags (clean 4-item navigation matching landing page)
  const allNavLinks = [
    {
      href: '/courses',
      label: 'Courses',
      feature: 'courses'
    },
    { href: '/masterclasses', label: 'Masterclasses', feature: 'masterclasses' },
    { href: '/labs', label: 'Learning Labs', feature: 'learning_labs' },
    { href: '/learning-pathways', label: 'Pathways', feature: 'learning_pathways' },
    { href: '/#features', label: 'Features' },
  ];

  // Filter nav links based on feature flags
  const navLinks = allNavLinks.filter(link =>
    !link.feature || isFeatureEnabled(link.feature)
  );

  const isActive = (path) => location.pathname.startsWith(path);
  // Use tenant branding or defaults
  const logoUrl = tenant?.branding?.logoUrl || DEFAULT_LOGO;
  const orgName = tenant?.name || 'Cerebro Core';
  const primaryColor = tenant?.branding?.primaryColor || '#14b8a6';

  return (
    <header
      className="sticky top-0 z-50 bg-slate-950/90 backdrop-blur-xl border-b border-slate-800"
      data-testid="main-header"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link
            to="/"
            className="flex items-center gap-3 shrink-0"
            data-testid="header-logo"
          >
            {!hideLogo && (
              <>
                <img
                  src={logoUrl ? logoUrl : CEREBRO_LOGO}
                  alt={orgName || "Cerebro Core"}
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = CEREBRO_LOGO;
                  }}
                  className="h-10 w-auto object-contain"
                />
                <span className="text-white font-bold text-lg hidden sm:block">
                  {orgName || "Cerebro Core"}
                </span>
              </>
            )}
            {hideLogo && (
              <span className="text-white font-bold text-lg hidden sm:block">
                {orgName || "Cerebro Core"}
              </span>
            )}
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-6 text-sm font-medium text-slate-300">
            {navLinks.map((link) => {
              const isCurrent = link.href === '/#features' ? false : isActive(link.href);
              return (
                <Link
                  key={link.href}
                  to={link.href}
                  className={`transition-colors ${
                    isCurrent
                      ? 'text-white font-semibold'
                      : 'hover:text-white'
                  }`}
                  style={isCurrent && !tenant?.isMaster && primaryColor ? { color: primaryColor } : undefined}
                  data-testid={`nav-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {link.label}
                </Link>
              );
            })}
          </nav>

          {/* Right Section */}
          <div className="flex items-center gap-3">
            {isAuthenticated ? (
              <>
                <NotificationCenter />
                <Link
                  to="/dashboard"
                  className="text-slate-900 text-sm font-semibold px-4 py-2 rounded-lg transition-all shadow hover:opacity-90 hidden sm:inline-flex items-center"
                  style={{ backgroundColor: primaryColor }}
                >
                  My Dashboard
                </Link>

                {/* User Menu */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      className="flex items-center gap-2 focus:outline-none"
                      data-testid="user-menu-btn"
                    >
                      <Avatar className="h-8 w-8 border border-slate-700">
                        <AvatarImage src={user?.picture} alt={user?.full_name} />
                        <AvatarFallback className="bg-slate-800 text-slate-200 text-xs">
                          {getInitials(user?.full_name)}
                        </AvatarFallback>
                      </Avatar>
                      <ChevronDown className="h-4 w-4 text-slate-400 hidden sm:block" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent
                    align="end"
                    className="w-56 bg-slate-900 border-slate-800 text-slate-200"
                  >
                    <div className="px-3 py-2 border-b border-slate-800">
                      <p className="text-sm font-medium text-white">{user?.full_name}</p>
                      <p className="text-xs text-slate-400">{user?.email}</p>
                    </div>
                    <DropdownMenuItem
                      onClick={() => navigate('/dashboard')}
                      className="cursor-pointer hover:bg-slate-800"
                    >
                      <LayoutDashboard className="h-4 w-4 mr-2" />
                      Dashboard
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => navigate('/my-courses')}
                      className="cursor-pointer hover:bg-slate-800"
                    >
                      <BookOpen className="h-4 w-4 mr-2" />
                      My Courses
                    </DropdownMenuItem>
                    {isFeatureEnabled('wishlist') && (
                    <DropdownMenuItem
                      onClick={() => navigate('/wishlist')}
                      className="cursor-pointer hover:bg-slate-800"
                    >
                      <Heart className="h-4 w-4 mr-2" />
                      Wishlist
                    </DropdownMenuItem>
                    )}
                    {isFeatureEnabled('certificates') && (
                    <DropdownMenuItem
                      onClick={() => navigate('/certificates')}
                      className="cursor-pointer hover:bg-slate-800"
                    >
                      <Award className="h-4 w-4 mr-2" />
                      Certificates
                    </DropdownMenuItem>
                    )}
                    <DropdownMenuItem
                      onClick={() => navigate('/profile')}
                      className="cursor-pointer hover:bg-slate-800"
                    >
                      <User className="h-4 w-4 mr-2" />
                      Profile
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => navigate('/settings')}
                      className="cursor-pointer hover:bg-slate-800"
                    >
                      <Settings className="h-4 w-4 mr-2" />
                      Settings
                    </DropdownMenuItem>
                    {isAdmin && (
                      <>
                        <DropdownMenuSeparator className="bg-slate-800" />
                        <DropdownMenuItem
                          onClick={() => navigate('/admin')}
                          className="cursor-pointer hover:bg-slate-800"
                        >
                          <LayoutDashboard className="h-4 w-4 mr-2" />
                          Admin Panel
                        </DropdownMenuItem>
                      </>
                    )}
                    <DropdownMenuSeparator className="bg-slate-800" />
                    <DropdownMenuItem
                      onClick={handleLogout}
                      className="cursor-pointer hover:bg-slate-800 text-red-400"
                    >
                      <LogOut className="h-4 w-4 mr-2" />
                      Sign out
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            ) : (
              <div className="flex items-center gap-3">
                <Link
                  to="/login"
                  className="text-slate-900 text-sm font-semibold px-4 py-2 rounded-lg transition-all shadow hover:opacity-90"
                  style={{ backgroundColor: primaryColor }}
                  data-testid="login-link"
                >
                  Sign In
                </Link>
                {tenant?.sso?.enabled && (
                  <Link
                    to="/enterprise-sso"
                    className="border border-slate-700 hover:bg-slate-800 text-white text-sm font-semibold px-4 py-2 rounded-lg transition-colors flex items-center gap-1.5"
                  >
                    <Shield className="h-3.5 w-3.5" />
                    <span>SSO</span>
                  </Link>
                )}
              </div>
            )}

            {/* Mobile Menu Button */}
            <button
              className="p-2 text-slate-400 hover:text-white md:hidden"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-btn"
            >
              {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden border-t border-slate-800 bg-slate-950">
          <nav className="px-4 py-4 space-y-2">
            {navLinks.map((link) => {
              const isCurrent = link.href === '/#features' ? false : isActive(link.href);
              return (
                <Link
                  key={link.href}
                  to={link.href}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`block px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    isCurrent
                      ? 'bg-slate-900 text-white font-semibold'
                      : 'text-slate-300 hover:bg-slate-900 hover:text-white'
                  }`}
                  style={isCurrent && !tenant?.isMaster && primaryColor ? { color: primaryColor } : undefined}
                >
                  {link.label}
                </Link>
              );
            })}
            {!isAuthenticated && (
              <div className="pt-2 border-t border-slate-800 flex flex-col gap-2">
                <Link
                  to="/login"
                  onClick={() => setMobileMenuOpen(false)}
                  className="text-slate-900 text-sm font-semibold px-4 py-2 rounded-lg transition-all shadow text-center"
                  style={{ backgroundColor: primaryColor }}
                >
                  Sign In
                </Link>
                {tenant?.sso?.enabled && (
                  <Link
                    to="/enterprise-sso"
                    onClick={() => setMobileMenuOpen(false)}
                    className="border border-slate-700 hover:bg-slate-800 text-white text-sm font-semibold px-4 py-2 rounded-lg transition-colors flex items-center justify-center gap-1.5"
                  >
                    <Shield className="h-3.5 w-3.5" />
                    <span>SSO</span>
                  </Link>
                )}
              </div>
            )}
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;
